import React, { useState } from 'react';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { homepageFAQs } from '../data/faqs';
import { sendRequirementsFormEmail } from '../utils/emailService';
import { scrollToElement } from '../utils/scrollUtils';
import { useContactInfo } from '../utils/contactObfuscation';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const contactInfo = useContactInfo();

  // SEO Data for Landing Page
  const seoData = {
    title: 'Get Your Custom Web & Mobile App Built | Professional Development Services | OctalChip',
    description: 'Transform your business with custom web and mobile applications. Get a free consultation and quote from our expert development team. Start your project today!',
    keywords: 'web development, mobile app development, custom software, development services, app development, website development, software development, development agency, custom applications, web applications, mobile applications, development quote, free consultation, development services, React, Node.js, Python, JavaScript, TypeScript, Flutter, React Native, iOS development, Android development, full-stack development, frontend development, backend development, database design, API development, cloud services, DevOps, UI/UX design, enterprise software, SaaS development, e-commerce development',
    canonical: 'https://www.octalchip.com/landingpage',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Custom Web & Mobile App Development Landing Page",
      "description": "Get your custom web and mobile applications built by expert developers. Free consultation and quote available.",
      "url": "https://www.octalchip.com/landingpage",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "Professional web and mobile app development services",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com"
        }
      }
    },
    faqData: homepageFAQs
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'Zap',
      title: 'Fast Delivery',
      description: 'Get your project delivered in 2-4 weeks with our agile development process'
    },
    {
      icon: 'Shield',
      title: 'Quality Guaranteed',
      description: '100% satisfaction guarantee with unlimited revisions until you\'re happy'
    },
    {
      icon: 'DollarSign',
      title: 'Competitive Pricing',
      description: 'Get professional development at 60% less than US agencies'
    },
    {
      icon: 'Users',
      title: 'Expert Team',
      description: 'Work with experienced developers who have 3-5+ years of expertise'
    }
  ];

  const services = [
    {
      title: 'Web Development',
      description: 'Custom websites, web applications, and e-commerce solutions',
      icon: 'Globe',
      features: ['React.js', 'Node.js', 'Python', 'Database Design']
    },
    {
      title: 'Mobile App Development',
      description: 'iOS, Android, and cross-platform mobile applications',
      icon: 'Smartphone',
      features: ['React Native', 'Flutter', 'Native iOS/Android', 'App Store Publishing']
    },
    {
      title: 'Backend Development',
      description: 'Robust server-side applications and API development',
      icon: 'Server',
      features: ['Node.js', 'Python/Django', 'Java/Spring', 'API Development']
    },
    {
      title: 'Cloud Development',
      description: 'Scalable cloud infrastructure and DevOps solutions',
      icon: 'Cloud',
      features: ['AWS/Azure/GCP', 'Docker/Kubernetes', 'CI/CD Pipelines', 'Serverless']
    },
    {
      title: 'UI/UX Design',
      description: 'User-centered design for exceptional digital experiences',
      icon: 'Palette',
      features: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems']
    },
    {
      title: 'AI & Automation',
      description: 'AI-powered solutions and business process automation',
      icon: 'Cpu',
      features: ['Machine Learning', 'Chatbots', 'Data Analytics', 'Process Automation']
    },
    {
      title: 'Workflow Automation',
      description: 'n8n, Zapier, and Make automation services',
      icon: 'Settings',
      features: ['n8n Automation', 'Zapier Integration', 'Make Automation', 'API Integration']
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      text: 'OctalChip delivered our e-commerce platform in just 3 weeks. The quality exceeded our expectations and the team was incredibly responsive.',
      rating: 5
    },
    {
      name: 'Mike Chen',
      company: 'InnovateCorp',
      text: 'We saved over $50,000 working with OctalChip compared to local agencies. The development quality was outstanding.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      company: 'GrowthLabs',
      text: 'The team at OctalChip understood our vision perfectly and delivered a mobile app that our users love. Highly recommended!',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section - Conversion Focused */}
      <section id="hero" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Professional Development Services
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight text-gray-800 px-4">
                  Get Your Custom Web & Mobile App Built by Experts
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-gray-600">
                  Transform your business with professional web and mobile applications. 
                  Get a free consultation and detailed quote in 24 hours.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {
                        // Silent fail - scrollToElement handles retries internally
                      });
                    }}
                  >
                    Get Free Quote Now
                  </Button>
                  <Button
                    variant="outline"
                    className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => {
                      window.open('https://calendly.com/business-octalchip/30min', '_blank');
                    }}
                  >
                    Schedule Free Call
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-6 text-sm">
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Free Consultation</span>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">24-Hour Response</span>
                  </div>
                  <div className="flex items-center justify-center lg:justify-start">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">No Hidden Costs</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <select 
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      required
                    >
                      <option value="">Select Project Type</option>
                      <option value="Web Development">Web Development</option>
                      <option value="Mobile App Development">Mobile App Development</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="AI/ML Solution">AI/ML Solution</option>
                      <option value="Workflow Automation">Workflow Automation</option>
                      <option value="Other">Other</option>
                    </select>
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Brief description of your project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-red-100 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary" 
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Why Choose OctalChip?</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Get professional development services with guaranteed quality and fast delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-4 sm:p-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Icon name={benefit.icon} size={24} className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#00d690]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">{benefit.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Our Development Services</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive development solutions to bring your ideas to life
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                  <Icon name={service.icon} size={24} className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#00d690]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <Icon name="Check" size={16} className="text-[#00d690] mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketing Strategies Section */}
      <section id="offers" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Special Offers & Free Trials</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Start your project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {/* Free Trial Card */}
            <div className="bg-white text-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">
                  POPULAR
                </span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">
                Get 2 hours of free development work to experience our quality and expertise
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>2 hours of free development</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>No commitment required</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Keep the code you receive</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Full project consultation</span>
                </li>
              </ul>
              <Button
                variant="primary"
                className="w-full bg-[#00d690] text-white hover:bg-[#00b876]"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Start Free Trial
              </Button>
            </div>

            {/* Free Credits Card */}
            <div className="bg-white text-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">
                  NEW
                </span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">
                Get $500 in development credits when you sign up for your first project
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>$500 free credits</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Valid for 30 days</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Apply to any service</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>No minimum spend</span>
                </li>
              </ul>
              <Button
                variant="primary"
                className="w-full bg-[#00d690] text-white hover:bg-[#00b876]"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Claim Free Credits
              </Button>
            </div>

            {/* Money Back Guarantee Card */}
            <div className="bg-white text-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">
                  GUARANTEE
                </span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">
                Not satisfied? Get your money back within 7 days of project completion
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>7-day money-back guarantee</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>No questions asked</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Keep all deliverables</span>
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                  <span>Full refund guarantee</span>
                </li>
              </ul>
              <Button
                variant="primary"
                className="w-full bg-[#00d690] text-white hover:bg-[#00b876]"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Start Risk-Free
              </Button>
            </div>
          </div>

          {/* Additional Offers */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Early Bird Discount */}
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Clock" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Early Bird Discount</h3>
                  <p className="text-[#00d690] font-medium">Save 20% on first project</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Book your project within 48 hours of consultation and get 20% off your total development cost.
              </p>
              <Button
                variant="outline"
                className="border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                onClick={() => {
                  window.open('https://calendly.com/business-octalchip/30min', '_blank');
                }}
              >
                Book Consultation Now
              </Button>
            </div>

            {/* Referral Program */}
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Users" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Referral Program</h3>
                  <p className="text-[#00d690] font-medium">Earn $200 per referral</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Refer a friend or business and both of you get $200 in development credits when they complete their first project.
              </p>
              <Button
                variant="outline"
                className="border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                onClick={async () => {
                  await scrollToElement('#get-quote', { 
                    smooth: true,
                    maxRetries: 15,
                    retryDelay: 150
                  }).catch(() => {
                    // Silent fail - scrollToElement handles retries internally
                  });
                }}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">What Our Clients Say</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-gray-800">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              Start Your Project Today
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-gray-800 px-4">Ready to Start Your Project?</h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 text-gray-600 px-4">
              Get a free consultation and detailed quote for your project. 
              Our team will help you bring your vision to life.
            </p>
            
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <select 
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  required
                >
                  <option value="">Select Project Type</option>
                  <option value="Web Development">Web Development</option>
                  <option value="Mobile App Development">Mobile App Development</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="AI/ML Solution">AI/ML Solution</option>
                  <option value="Other">Other</option>
                </select>
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your project requirements, goals, and timeline..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary" 
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center justify-center">
                <Icon name="Phone" size={20} className="text-[#00d690] mr-2" />
                {contactInfo.phoneLink && (
                  <a href={contactInfo.phoneLink} className="text-gray-700 hover:text-[#00d690]">
                    {contactInfo.phone}
                  </a>
                )}
              </div>
              <div className="flex items-center justify-center">
                <Icon name="Mail" size={20} className="text-[#00d690] mr-2" />
                <a href="mailto:business@octalchip.com" className="text-gray-700 hover:text-[#00d690]">
                  business@octalchip.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={homepageFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our development services, process, and working with OctalChip."
      />

      <Footer />
    </div>
  );
};

export default LandingPage; 